import React, { useEffect, useRef, useState } from 'react';
import DistributorAddressFields from './DistributorAddressFields';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';

export default function NewDistributorCustomer({
  customer,
  onChange,
  onSubmit,
  onClose,
  creating,
  dirty,
  error,
  inputClass,
  t,
}) {
  const [discarding, setDiscarding] = useState(false);
  const nameRef = useRef(null);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  const close = () => {
    if (creating) return;
    if (dirty) setDiscarding(true);
    else onClose();
  };
  const { dialogRef, titleId } = useAccessibleDialog({
    open: true,
    onClose: close,
    closeDisabled: creating,
    initialFocusRef: nameRef,
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="me-dw-card me-dw-create w-full max-w-xl max-h-[90vh] overflow-y-auto p-5 sm:p-6"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-lg font-bold text-[#153d48]">
            {t('New Customer')}
          </h2>
          <button type="button" onClick={close} disabled={creating} className="me-dw-link text-sm">
            {t('Close')}
          </button>
        </div>
        <p className="mb-5 text-sm text-[#5e737a]">
          {t(
            'Add a distributor customer here. This customer list is separate from your MedEffects client clinics and shared by your distributor portal users.'
          )}
        </p>
        <form onSubmit={onSubmit}>
          <fieldset disabled={creating} className="space-y-4">
            {error && (
              <p role="alert" className="text-red-700">
                {t(error)}
              </p>
            )}
            <label className="block">
              {t('Customer name')}
              <input
                ref={nameRef}
                className={inputClass}
                value={customer.name}
                minLength={2}
                maxLength={120}
                onChange={(event) => onChange('name', event.target.value)}
                required
              />
            </label>
            <DistributorAddressFields
              address={customer}
              onChange={onChange}
              {...{ t, inputClass }}
            />
            <div className="flex items-center justify-end gap-4 border-t border-[#e1ebeb] pt-4">
              <button type="button" className="me-dw-link text-sm" onClick={close}>
                {t('Cancel')}
              </button>
              <button type="submit" className="me-dw-primary">
                {t(creating ? 'Creating...' : 'Create customer')}
              </button>
            </div>
          </fieldset>
        </form>
        {discarding && (
          <div
            role="region"
            aria-label={t('Discard customer draft?')}
            className="me-dw-subpanel mt-4 p-4"
          >
            <p className="text-sm">{t('Closing discards this customer draft.')}</p>
            <div className="mt-3 flex flex-wrap gap-4">
              <button type="button" className="me-dw-link" onClick={() => setDiscarding(false)}>
                {t('Keep editing')}
              </button>
              <button type="button" className="me-dw-primary" onClick={onClose}>
                {t('Discard draft')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
